import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { keys } from "../../api/queryKeys.ts";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import CodeBlock from "../../components/CodeBlock.tsx";
import FormattedDate from "../../components/FormattedDate";
import EmptyTableRow from "../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client.ts";
import { humanize } from "../../utils/format.ts";

interface TenantDataDialogProps {
  tenantId: string;
  tenantName: string;
  objectType: string;
  onClose: () => void;
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

function TenantDataDialog({
  tenantId,
  tenantName,
  objectType,
  onClose,
}: TenantDataDialogProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const { data, isLoading } = useQuery({
    queryKey: keys.tenantData.page(tenantId, objectType, rowsPerPage, page),
    queryFn: () =>
      api.getTenantData(tenantId, objectType, rowsPerPage, page * rowsPerPage),
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {humanize(objectType)}: {tenantName}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <>
            <ScrollTable containerSx={{ maxHeight: "60vh" }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>External ID</TableCell>
                  <TableCell>Properties</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.records.map((record) => {
                  const dateStr =
                    objectType === "activities"
                      ? (record.properties.hs_timestamp as string)
                      : (record.properties.createdate as string);
                  return (
                    <TableRow key={record.id}>
                      <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                        {record.external_id}
                      </TableCell>
                      <TableCell>
                        <CodeBlock maxHeight={200} sx={{ m: 0, p: 1, fontSize: "0.75rem" }}>
                          {JSON.stringify(record.properties, null, 2)}
                        </CodeBlock>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                        <FormattedDate value={dateStr} format="datetime" fallback="—" />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data?.records.length === 0 && (
                  <EmptyTableRow colSpan={3}>No records found</EmptyTableRow>
                )}
              </TableBody>
            </ScrollTable>
            <TablePagination
              component="div"
              count={data?.total ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default TenantDataDialog;
